<template>
  <div class="notif" ref="rootEl">
    <button
      type="button"
      class="notif__bell"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="bellAriaLabel"
      @click.stop="togglePanel"
    >
      <font-awesome-icon icon="bell" class="notif__icon" />
      <span v-if="badgeText" class="notif__badge">{{ badgeText }}</span>
    </button>

    <div v-if="isOpen" class="notif__panel" role="dialog" aria-label="Уведомления">
      <div class="notif__header">
        <button
          v-if="view !== VIEWS.MENU"
          type="button"
          class="notif__back"
          aria-label="Назад"
          @click="goBack"
        >
          <font-awesome-icon icon="arrow-left" />
        </button>
        <h3 class="notif__title">{{ panelTitle }}</h3>
        <label
          v-if="view === VIEWS.CHANNEL && selectedChannel"
          class="notif__toggle"
          title="Push-уведомления канала"
        >
          <input
            type="checkbox"
            :checked="channelPushEnabled"
            :disabled="togglePending"
            @change="onToggleChannelPush($event.target.checked)"
          />
          <span>уведомления</span>
        </label>
      </div>

      <div class="notif__body">
        <ul v-if="view === VIEWS.MENU" class="notif__sections">
          <li>
            <button type="button" class="notif__section" @click="openView(VIEWS.PERSONAL)">
              <span>Личные уведомления</span>
              <font-awesome-icon icon="angles-right" class="notif__chevron" />
            </button>
          </li>
          <li>
            <button type="button" class="notif__section" @click="openView(VIEWS.ROLE)">
              <span>{{ roleTitle }}</span>
              <font-awesome-icon icon="angles-right" class="notif__chevron" />
            </button>
          </li>
          <li>
            <button type="button" class="notif__section" @click="openView(VIEWS.CHANNELS)">
              <span>Уведомления в каналах</span>
              <font-awesome-icon icon="angles-right" class="notif__chevron" />
            </button>
          </li>
        </ul>

        <div v-else-if="view === VIEWS.CHANNELS">
          <p v-if="channelsPending" class="notif__hint">Загрузка каналов…</p>
          <p v-else-if="channelsError" class="notif__error">Не удалось загрузить каналы</p>
          <p v-else-if="!channels.length" class="notif__hint">Нет каналов</p>
          <ul v-else class="notif__list">
            <li v-for="ch in channels" :key="ch.id">
              <button type="button" class="notif__item" @click="openChannel(ch)">
                <span class="notif__item-text">{{ ch.name || `Канал #${ch.id}` }}</span>
                <span v-if="channelUnreadBadge(ch)" class="notif__item-unread">{{ channelUnreadBadge(ch) }}</span>
                <font-awesome-icon icon="angles-right" class="notif__chevron" />
              </button>
            </li>
          </ul>
        </div>

        <NotificationMessageList
          v-else
          :messages="messages"
          :pending="messagesPending"
          :error="messagesError"
          :has-params="hasMessageFilterParams(messagesParams)"
          @select="onMessageClick"
        />
      </div>

      <div class="notif__footer">
        <button type="button" class="notif__all" @click="openInboxPage">
          Все уведомления
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { MESSAGES_UNREAD_QUERY_KEY } from '@/queryClient'
import {
  NOTIFICATION_VIEWS as VIEWS,
  hasMessageFilterParams,
  channelUnreadBadge,
} from '@/components/notifications/notificationLogic.mjs'
import { notificationRoute } from '@/components/notifications/notificationWsLogic.mjs'
import { syncWebPushSubscription } from '@/backend/webPushClient'
import NotificationMessageList from '@/components/notifications/NotificationMessageList.vue'
import { useNotificationInbox } from '@/composables/useNotificationInbox'
import { useUnreadNotifications } from '@/composables/useUnreadNotifications'

const router = useRouter()

const rootEl = ref(null)
const isOpen = ref(false)
const view = ref(VIEWS.MENU)
const selectedChannel = ref(null)

const { unreadCount, badgeText } = useUnreadNotifications()
const {
  roleTitle,
  messagesParams,
  messages,
  messagesPending,
  messagesError,
  channels,
  channelsPending,
  channelsError,
  channelPushEnabled,
  togglePending,
  panelTitle,
  onToggleChannelPush,
  markIfUnread,
  queryClient,
} = useNotificationInbox({
  active: isOpen,
  view,
  selectedChannel,
})

const bellAriaLabel = computed(() => {
  const n = unreadCount.value
  if (n > 0) return `Уведомления, ${n} непрочитанных`
  return 'Уведомления'
})

function openView(next) {
  view.value = next
  selectedChannel.value = null
}

function openChannel(ch) {
  selectedChannel.value = ch
  view.value = VIEWS.CHANNEL
}

function goBack() {
  if (view.value === VIEWS.CHANNEL) {
    view.value = VIEWS.CHANNELS
    selectedChannel.value = null
    return
  }
  view.value = VIEWS.MENU
  selectedChannel.value = null
}

function togglePanel() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    view.value = VIEWS.MENU
    selectedChannel.value = null
    queryClient.invalidateQueries({ queryKey: [MESSAGES_UNREAD_QUERY_KEY] })
    syncWebPushSubscription({ requestPermission: true })
  }
}

function openInboxPage() {
  isOpen.value = false
  router.push({ name: 'Notifications' }).catch(() => {})
}

function onMessageClick(msg) {
  markIfUnread(msg)
  const loc = notificationRoute(msg)
  if (!loc) return
  isOpen.value = false
  router.push(loc).catch(() => {})
}

function onDocClick(event) {
  if (!isOpen.value) return
  // Vue может заменить кликнутый узел прямо во время всплытия click (меню → подсписок):
  // event.target уже отсоединён, contains() даёт false и панель ошибочно закрывается.
  // Отсоединённый target = клик по нашему UI, вне панели таких не бывает.
  if (event.target && !event.target.isConnected) return
  if (rootEl.value && !rootEl.value.contains(event.target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
})
</script>

<style scoped>
.notif {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.notif__bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.notif__bell:hover {
  background: #f3f4f6;
  color: #111827;
}

.notif__icon {
  font-size: 1.05rem;
}

.notif__badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 9999px;
  background: #dc2626;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 17px;
  text-align: center;
  box-shadow: 0 0 0 2px #fefefe;
}

.notif__panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 360px;
  max-height: min(70vh, 480px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(17, 24, 39, 0.12);
  z-index: 120;
  overflow: hidden;
}

.notif__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}

.notif__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
}

.notif__back:hover {
  background: #e5e7eb;
}

.notif__title {
  flex: 1;
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}

.notif__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6b7280;
  cursor: pointer;
  user-select: none;
}

.notif__body {
  overflow-y: auto;
  flex: 1;
}

.notif__sections,
.notif__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.notif__section,
.notif__item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #1f2937;
  font-size: 0.9rem;
}

.notif__section:hover,
.notif__item:hover {
  background: #f9fafb;
}

.notif__chevron {
  color: #9ca3af;
  font-size: 0.75rem;
}

.notif__item-text {
  flex: 1;
  min-width: 0;
  display: block;
  font-size: 0.875rem;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif__item-unread {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 9999px;
  background: #dc2626;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  flex-shrink: 0;
}

.notif__hint,
.notif__error {
  margin: 0;
  padding: 28px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: #9ca3af;
}

.notif__error {
  color: #dc2626;
}

.notif__footer {
  border-top: 1px solid #f3f4f6;
  padding: 8px 10px;
  background: #fafafa;
}

.notif__all {
  width: 100%;
  border: none;
  background: transparent;
  color: #4f46e5;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
}

.notif__all:hover {
  background: #eef2ff;
}

@media screen and (max-width: 426px) {
  .notif__panel {
    right: initial;
    margin-left: calc(50% - 50vw);
    width: calc(100vw - 200%);
  }
}
</style>
